// A translated segment is a sentence with inline formatting serialised as paired
// tags (`<t0>…</t0>`). Rendering that raw string in an RTL field lets the Unicode
// bidi algorithm mirror/reorder the ASCII tags against the RTL text, so it looks
// scrambled even though the logical string is correct. These helpers split a
// value into tag vs text runs so each can be bidi-isolated (display) or edited
// independently with the tags kept as immutable tokens (edit).

const TAG_TOKEN = /<\/?t\d+>/g;

export type TagRun = { kind: 'text'; text: string } | { kind: 'tag'; raw: string };

// Runs for display: tags and non-empty text, in order. Empty gaps are dropped.
export function tokenizeTags(value: string): TagRun[] {
  const runs: TagRun[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  TAG_TOKEN.lastIndex = 0;
  while ((match = TAG_TOKEN.exec(value)) !== null) {
    if (match.index > cursor) {
      runs.push({ kind: 'text', text: value.slice(cursor, match.index) });
    }
    runs.push({ kind: 'tag', raw: match[0] });
    cursor = match.index + match[0].length;
  }
  if (cursor < value.length) {
    runs.push({ kind: 'text', text: value.slice(cursor) });
  }
  return runs;
}

// Alternating split for editing: `texts.length === tags.length + 1`, so the
// value is `texts[0] + tags[0] + texts[1] + … + texts[n]`. Text slots (including
// empty ones at the edges/between adjacent tags) are the only editable parts;
// tags are fixed tokens the author can't touch.
export function splitForEdit(value: string): { texts: string[]; tags: string[] } {
  const texts: string[] = [];
  const tags: string[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  TAG_TOKEN.lastIndex = 0;
  while ((match = TAG_TOKEN.exec(value)) !== null) {
    texts.push(value.slice(cursor, match.index));
    tags.push(match[0]);
    cursor = match.index + match[0].length;
  }
  texts.push(value.slice(cursor));
  return { texts, tags };
}

// Reassemble an edited split back into a single tagged string, interleaving the
// fixed tags between the (possibly edited) text slots.
export function joinForEdit(texts: string[], tags: string[]): string {
  let out = '';
  for (let i = 0; i < tags.length; i += 1) {
    out += texts[i] ?? '';
    out += tags[i];
  }
  out += texts[texts.length - 1] ?? '';
  return out;
}
